import React from 'react';
import { useSearchParams } from 'react-router-dom';
import { Box, Typography, Tabs, Tab, Paper, Alert } from '@mui/material';
import { ReceiptLong as ReceiptLongIcon } from '@mui/icons-material';
import { useAuth } from '../../context/AuthContext';
import CountryMaster from './CountryMaster';
import CurrencyMaster from './CurrencyMaster';
import VendorMaster from './VendorMaster';
import ExpenseCategoryMaster from './ExpenseCategoryMaster';
import ExpenseHeadMaster from './ExpenseHeadMaster';
import PaidViaMaster from './PaidViaMaster';
import JobNameMaster from './JobNameMaster';
import EmployeeExpenseSettings from './EmployeeExpenseSettings';

// Expense masters & settings (Phase 1). Tabs shown depend on the user's role / access:
//   HR admin            : Countries, Currencies (edit), Employee Settings (approvers, home country)
//   Expense Accounts    : Vendors, Categories, Heads, Paid Via, Job Names (edit),
//                         Countries/Currencies (view), Employee Settings (wallet currency)
// The backend enforces all permissions; this only controls what is displayed.
const ExpenseMasters = () => {
  const { user } = useAuth();
  const [searchParams, setSearchParams] = useSearchParams();

  const isAdmin = user?.primaryRole === 'admin';
  const isAccounts = user?.access?.expenseAccounts === true;

  const tabs = [
    { key: 'countries', label: 'Countries', show: isAdmin || isAccounts, render: () => <CountryMaster canEdit={isAdmin} /> },
    { key: 'currencies', label: 'Currencies', show: isAdmin || isAccounts, render: () => <CurrencyMaster canEdit={isAdmin} /> },
    { key: 'vendors', label: 'Vendors', show: isAccounts, render: () => <VendorMaster canEdit={isAccounts} /> },
    { key: 'categories', label: 'Categories', show: isAccounts, render: () => <ExpenseCategoryMaster canEdit={isAccounts} /> },
    { key: 'heads', label: 'Heads', show: isAccounts, render: () => <ExpenseHeadMaster canEdit={isAccounts} /> },
    { key: 'paid-via', label: 'Paid Via', show: isAccounts, render: () => <PaidViaMaster canEdit={isAccounts} /> },
    { key: 'job-names', label: 'Job Names', show: isAccounts, render: () => <JobNameMaster canEdit={isAccounts} /> },
    { key: 'employee-settings', label: 'Employee Settings', show: isAdmin || isAccounts, render: () => <EmployeeExpenseSettings isAdmin={isAdmin} isAccounts={isAccounts} /> },
  ].filter((t) => t.show);

  if (user && user.access === undefined) {
    return <Box sx={{ p: 3 }}><Typography>Loading...</Typography></Box>;
  }

  if (!tabs.length) {
    return (
      <Box sx={{ p: 3 }}>
        <Alert severity="warning">You do not have access to Expense masters.</Alert>
      </Box>
    );
  }

  const requested = searchParams.get('tab');
  const active = tabs.find((t) => t.key === requested) || tabs[0];

  return (
    <Box sx={{ bgcolor: 'background.default', p: 3 }}>
      <Box sx={{ mb: 3 }}>
        <Typography variant="h4" component="h1" sx={{ fontWeight: 600, fontSize: '30px', lineHeight: 1.5, letterSpacing: 1, display: 'flex', alignItems: 'center', gap: 1 }}>
          <ReceiptLongIcon color="primary" />
          Expense Masters
        </Typography>
        <Typography variant="body1" color="text.secondary">
          Master data and employee settings for the Expense module
        </Typography>
      </Box>

      <Paper sx={{ mb: 3 }}>
        <Tabs
          value={active.key}
          onChange={(e, key) => setSearchParams({ tab: key })}
          variant="scrollable"
          scrollButtons="auto"
        >
          {tabs.map((t) => <Tab key={t.key} value={t.key} label={t.label} sx={{ textTransform: 'none' }} />)}
        </Tabs>
      </Paper>

      <Box key={active.key}>{active.render()}</Box>
    </Box>
  );
};

export default ExpenseMasters;
